import { lazy, Suspense, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { getApiUrl } from "@utils/baseUrl";
import { useDispatch, useSelector } from "react-redux";
import LoaderImg from "@assets/images/loader.gif";
import * as Sentry from "@sentry/browser";
import { corporateHierarchyCachingHandler } from "@redux/CompanyProfile/companyProfileSlice";
import { TransitionHOC } from "@hoc/TransitionHoc/TransitionHoc";
import styles from "./corporate.module.scss";
import ReactCountryFlag from "react-country-flag";
import { navigateTo } from "@containers/CompanyProfile/utils/helper";
import nodataimage from "./../../../../assets/images/common/no-data.png";

const HeirarchyChart = lazy(() => import("./HeirarchyChart/heirarchyFlowChart"));

const companyProfileAPIEndPoints = {
  getAllSubsidiary: getApiUrl("getAllConnections"),
};

export const CompanyProfileHierarchy = () => {
  const { companyname } = useParams();
  const [data, setData] = useState({});
  const [loader, setLoader] = useState(true);
  const { countDetails, corporateHierarchyCaching } = useSelector((state) => state.companyProfile);
  const currentCompanyId = Array.isArray(countDetails?.elastic_data_id) ? countDetails?.elastic_data_id[0] : countDetails?.elastic_data_id;
  const [expandedNodes, setExpandedNodes] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (countDetails?.elastic_data_id && !Object.keys(corporateHierarchyCaching).length) {
      getAllSubsidiary();
    } else {
      setData(corporateHierarchyCaching);
      setExpandedNodes({ [corporateHierarchyCaching?.parent_node?.[0]?.id]: true });
      setLoader(false);
    }
  }, [countDetails, corporateHierarchyCaching]);

  const getAllSubsidiary = () => {
    setLoader(true);
    const requestObj = {
      company_slug: companyname,
      company_id: Array.isArray(countDetails?.elastic_data_id) ? countDetails?.elastic_data_id[0] : countDetails?.elastic_data_id,
    };

    const getAllSubsidiary = companyProfileAPIEndPoints.getAllSubsidiary;
    axios
      .post(getAllSubsidiary, requestObj)
      .then((res) => {
        dispatch(corporateHierarchyCachingHandler(res.data.response));
        setData(res.data.response);
        return res;
      })
      .then((res) => {
        const parentId = res?.data?.response?.parent_node?.[0]?.id;
        expandedNodes[parentId] = true;
        setExpandedNodes(expandedNodes);
      })
      .catch((err) => {
        Sentry.captureException(err);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleNodeExpansion = (data) => {
    if (expandedNodes[data.id]) {
      setExpandedNodes({ [data.id]: false });
    } else setExpandedNodes({ [data.id]: true });
  };

  return (
    <div className="mt-4">
      <TransitionHOC>
        {loader ? (
          <div className="d-flex align-items-center justify-content-center mt-5">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        ) : data?.parent_node?.length === 0 ? (
          <div className="d-block text-center w-100 p-5">
            <div className="card px-2 pt-4 pb-1 w-25 m-auto no-data-card border-0">
              <div className="text-center card-body p-2">
                <div className="header-image">
                  <img className="img-fluid" src={nodataimage} alt="..."></img>
                </div>
                <p className="mt-4">No Corporate Hierarchy.</p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="tw-p-4">
              {data?.parent_node?.map((parent) => (
                <li className={styles.tree_view} key={parent.id}>
                  <div className="tw-pb-3">
                    <div className={`tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-w-[650px] tw-py-2 tw-px-3 ${parent?.id === currentCompanyId ? "tw-border tw-border-primary-purple-400 tw-rounded-lg tw-bg-primary-purple-100/20 tw-ml-2" : ""}`} onClick={() => handleNodeExpansion(parent)}>
                      {expandedNodes[parent?.id] ? (
                        <span>
                          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#102828">
                            <path d="M290-450h380v-60H290v60Zm-77.69 310Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h535.38Q778-820 799-799q21 21 21 51.31v535.38Q820-182 799-161q-21 21-51.31 21H212.31Zm0-60h535.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-535.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM200-760v560-560Z" />
                          </svg>
                        </span>
                      ) : (
                        <span>
                          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#102828">
                            <path d="M450-290h60v-160h160v-60H510v-160h-60v160H290v60h160v160ZM212.31-140Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h535.38Q778-820 799-799q21 21 21 51.31v535.38Q820-182 799-161q-21 21-51.31 21H212.31Zm0-60h535.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-535.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM200-760v560-560Z" />
                          </svg>
                        </span>
                      )}
                      <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
                        <div className="tw-flex tw-gap-4 tw-capitalize">
                          <div
                            onClick={() => {
                              if (parent?.id !== currentCompanyId) navigateTo(data?.nodes_information[parent?.id], currentCompanyId);
                            }}
                            className={`tw-text-sm tw-font-medium tw-w-4/5 ${data?.nodes_information[parent?.id]?.company_id ? "tw-text-primary-purple-500 tw-cursor-pointer tw-underline" : "tw-text-gray-700"}`}>
                            {data?.nodes_information[parent?.id].company_name}
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-1 tw-flex-start tw-w-1/5">
                            <ReactCountryFlag countryCode={data?.nodes_information[parent?.id].country_code} svg />
                            <span className="tw-text-xs tw-text-gray-900">{data?.nodes_information[parent?.id].country}</span>
                          </div>
                        </div>
                        {data?.nodes_information[parent?.id].full_address ? (
                          <div className="tw-flex tw-gap-1 tw-items-center tw-text-xs tw-text-gray-500 tw-capitalize">
                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#667085">
                              <path d="M480-116q-103.38 0-166.69-27.1T250-214.23q0-23.31 21.04-43.08t59.35-34.23l42.23 38.85q-18.85 6.15-39.51 16.34-20.65 10.2-31.88 22.73 4.92 15.93 61.04 30.77Q418.38-168 479.81-168q65.42 0 120.54-14.85 55.11-14.84 58.04-33.77-11.08-10.15-33-20.34-21.93-10.19-40.77-16.35l42.61-39.46q40.69 14.08 61.73 34.85Q710-237.15 710-214.4q0 44.11-63.31 71.25Q583.38-116 480-116Zm1-185q95.38-72.31 143.19-146.19Q672-521.08 672-590.92q0-99.53-61.81-150.3-61.81-50.78-130-50.78T350-741.19q-62 50.8-62 150.38 0 66.04 48.19 139.12T481-301Zm-1 65q-122.93-91.89-183.46-181.64Q236-507.38 236-590.86q0-61.83 22.08-109.06 22.08-47.23 57.38-79.12 35.31-31.88 78.72-48.42Q437.58-844 480.06-844t85.82 16.54q43.35 16.54 78.66 48.42 35.3 31.89 57.38 79.15Q724-652.63 724-590.92q0 83.49-60.54 173.25Q602.93-327.91 480-236Zm.19-296.23q26.73 0 45.42-18.5 18.7-18.5 18.7-45.62 0-27.11-18.74-45.8-18.74-18.7-45.57-18.7-26.54 0-45.42 19.24-18.89 19.25-18.89 45.07 0 26.31 18.89 45.31 18.88 19 45.61 19Zm-.19-64.31Z" />
                            </svg>
                            <div>{data?.nodes_information[parent?.id].full_address}</div>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                    <ul className="tw-mt-5">
                      {expandedNodes[parent?.id] &&
                        parent.children?.map((nested) => (
                          <Suspense key={nested.name} fallback={""}>
                            <HeirarchyChart data={nested} allParentChildData={data?.all_parent_child_relations} nodesInfo={data?.nodes_information} currentCompanyId={currentCompanyId} />
                          </Suspense>
                        ))}
                    </ul>
                  </div>
                </li>
              ))}
            </div>
          </div>
        )}
      </TransitionHOC>
    </div>
  );
};
